import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MessageCircle, Send, Sparkles, Lock, FileText, LineChart as LineChartIcon } from "lucide-react";

export default function AnalystChat() {
  const { user } = useAuth();
  const isEnterprise = user?.tier === "enterprise";
  const [commodities, setCommodities] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [turns, setTurns] = useState([]);
  const [commodityCode, setCommodityCode] = useState("WHEAT");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  const loadSessions = async () => {
    try { const { data } = await api.get("/analyst-chat/sessions"); setSessions(data); }
    catch {}
  };

  const openSession = async (id) => {
    setSessionId(id); setTurns([]);
    try {
      const { data } = await api.get(`/analyst-chat/sessions/${id}`);
      setTurns(data.turns || []);
      if (data.session?.last_commodity_code) setCommodityCode(data.session.last_commodity_code);
    } catch { toast.error("Failed to load session"); }
  };

  const newSession = () => { setSessionId(null); setTurns([]); };

  useEffect(() => {
    if (!isEnterprise) return;
    (async () => {
      try {
        const [c, s] = await Promise.all([api.get("/commodities"), api.get("/analyst-chat/sessions")]);
        setCommodities(c.data); setSessions(s.data);
      } catch {}
    })();
  }, [isEnterprise]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [turns.length]);

  const send = async () => {
    const text = message.trim();
    if (!text || busy) return;
    setBusy(true);
    const now = new Date().toISOString();
    const optimistic = { id: `u-${now}`, role: "user", content: text, created_at: now, commodity_code: commodityCode };
    setTurns(prev => [...prev, optimistic]);
    setMessage("");
    try {
      const { data } = await api.post("/analyst-chat", { session_id: sessionId, commodity_code: commodityCode, message: text });
      if (!sessionId) setSessionId(data.session_id);
      setTurns(prev => [...prev, { id: `a-${data.created_at}`, role: "assistant", content: data.reply, created_at: data.created_at, sources: data.sources, commodity_code: commodityCode }]);
      loadSessions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send");
      setTurns(prev => prev.filter(t => t.id !== optimistic.id));
    } finally { setBusy(false); }
  };

  if (!isEnterprise) {
    return (
      <div className="mx-auto max-w-2xl">
        <Card className="border-primary/40 bg-primary/5" data-testid="analyst-chat-upgrade">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-display"><Lock className="h-5 w-5 text-primary" />Analyst Chat is an Enterprise feature</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">Ask sourced questions to our AI analyst. Every answer cites the underlying quote, analyst note, or EOD recap it draws from — so your procurement team can trust the reasoning.</p>
            <ul className="ml-4 list-disc space-y-1 text-sm text-muted-foreground">
              <li>Multi-turn conversation with persistent memory per session</li>
              <li>Inline citations against seeded analyst notes + live quotes + EOD recaps</li>
              <li>Refuses off-domain questions — stays focused on agri procurement</li>
            </ul>
            <Button data-testid="upgrade-enterprise-btn" onClick={() => (window.location.href = "/app/pricing")}>Upgrade to Enterprise</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="grid gap-4 lg:grid-cols-[240px_1fr]">
      {/* Session list */}
      <Card className="border-border/60 bg-card/60">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Sessions</CardTitle>
          <Button size="sm" variant="ghost" data-testid="new-session-btn" onClick={newSession} title="Start new">+ New</Button>
        </CardHeader>
        <CardContent className="space-y-1 p-2">
          {sessions.length === 0 && <p className="p-3 text-center text-xs text-muted-foreground">No conversations yet.</p>}
          {sessions.map(s => (
            <button
              key={s.session_id}
              data-testid={`session-${s.session_id}`}
              onClick={() => openSession(s.session_id)}
              className={`w-full rounded-md border p-2 text-left transition ${sessionId === s.session_id ? "border-primary bg-primary/10" : "border-border/40 hover:bg-muted/50"}`}
            >
              <div className="line-clamp-1 text-xs font-semibold">{s.title || "Conversation"}</div>
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                {s.last_commodity_code || "—"} · {new Date(s.updated_at).toLocaleDateString()}
              </div>
            </button>
          ))}
        </CardContent>
      </Card>

      {/* Chat pane */}
      <Card className="flex min-h-[70vh] flex-col border-border/60 bg-card/60">
        <CardHeader>
          <div className="flex flex-wrap items-center gap-3">
            <CardTitle className="flex items-center gap-2 font-display text-lg"><Sparkles className="h-5 w-5 text-primary" />Analyst Chat</CardTitle>
            <Badge variant="outline" className="uppercase text-[10px]">Enterprise · Claude Sonnet 5</Badge>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Focus</span>
              <Select value={commodityCode} onValueChange={setCommodityCode}>
                <SelectTrigger data-testid="chat-commodity-select" className="h-9 w-56"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {commodities.map(c => <SelectItem key={c.code} value={c.code}>{c.name} ({c.code})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col gap-3 overflow-hidden">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto rounded-md border border-border/40 bg-background/40 p-3" data-testid="chat-messages">
            {turns.length === 0 && (
              <div className="grid h-full place-items-center text-center text-sm text-muted-foreground">
                <div>
                  <MessageCircle className="mx-auto mb-3 h-8 w-8 text-primary" />
                  Ask about {commodityCode}: forward cover, hedging, weather risk, supplier options.<br />
                  Every answer cites its sources.
                </div>
              </div>
            )}
            {turns.map(t => (
              <div key={t.id} data-testid={`turn-${t.role}`} className={`mb-3 flex gap-2 ${t.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-md border px-3 py-2 text-sm ${t.role === "user" ? "border-primary/40 bg-primary/10" : "border-border/60 bg-card/70"}`}>
                  <div className="whitespace-pre-wrap leading-relaxed">{t.content}</div>
                  {t.role === "assistant" && t.sources && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {t.sources.quote && <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/60 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground"><LineChartIcon className="h-3 w-3" />Quote</span>}
                      {(t.sources.notes || []).map(n => (
                        <span key={n} className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/60 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground"><FileText className="h-3 w-3" />{n?.slice(0, 40)}</span>
                      ))}
                      {t.sources.eod_recap && <span className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-background/60 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">EOD recap</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && <div className="text-[11px] text-muted-foreground">Analyst is thinking…</div>}
            <div ref={bottomRef} />
          </div>
          {/* Composer */}
          <div className="flex items-center gap-2">
            <Input
              data-testid="chat-input"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={`Ask about ${commodityCode}…`}
              disabled={busy}
            />
            <Button data-testid="chat-send-btn" onClick={send} disabled={busy || !message.trim()} className="gap-1.5">
              <Send className="h-4 w-4" />Send
            </Button>
          </div>
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Market insight, not financial advice.</p>
        </CardContent>
      </Card>
    </div>
  );
}
